﻿<template v-if="!isMobile">
  <IonHeader class="drawer-open">
    <div class="desktop-toolbar custom-navbar">
      <div slot="start" class="drawer-toggle-slot">
        <ZenQuickSearch
          v-if="shouldShowQuickSearch"
          @navigate="handleSearchNavigation"
          :route-is-student-home="routeIsStudentHome"
        />
      </div>
      <div v-if="isDevelopment" class="development-mode-indicator">
        {{ t('nav.developmentMode', { appName }) }}
      </div>

      <div slot="end" class="navigation-section">
        <Button v-if="shouldShowChat" @click="navigateToBugForm" class="ward-button">
          <p class="ward-button-title">{{ t('nav.feedback').toLocaleUpperCase() }}</p>
        </Button>
        <ToolsTopBar v-if="shouldShowTools" />

        <IonButtons class="toolbar-buttons">
          <Button
            v-if="shouldShowChatbot"
            @click="navigateToChatbot"
            class="toggle-btn"
            shape="round"
          >
            <IconRenderer icon="sparkles-outline" slot="icon-only" />
          </Button>
          <Button v-if="shouldShowChat" @click="navigateToChat" class="toggle-btn" shape="round">
            <IconRenderer name="chatbubble-ellipses-outline" slot="icon-only" />
          </Button>
          <Button
            v-if="isDevelopment"
            :color="'medium'"
            @click="toggleMockupMode"
            class="toggle-btn"
            shape="round"
          >
            <IconRenderer :icon="isMockupMode ? 'flask-outline' : 'flask'" slot="icon-only" />
          </Button>

          <UserAvatarPopover :current-theme="currentTheme" @theme-toggled="onThemeToggled" />
        </IonButtons>
      </div>
    </div>
  </IonHeader>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue';
import { IonHeader, IonButtons } from '@ionic/vue';
import UserAvatarPopover from '@/features/common/user/user-avatar/components/UserAvatarPopover.vue';
import { storageService } from '@/common/services/storage/storageService';
import { useMockupMode } from '@/common/services/mockup';
import { useLayout } from '@/layouts/composables/useLayout';
import { useRouter } from 'vue-router';
import ToolsTopBar from '@/layouts/navigation-layout/desktop/ToolsTopBar.vue';
import { IconRenderer, Button, ZenQuickSearch } from '@/common/components/ui';
import { useLocale } from '@/common/composables/useLocale';

const appName = import.meta.env.VITE_APP_NAME || 'WARD';
const glpiBaseUrl = import.meta.env.VITE_GLPI_BASE_URL || 'https://glpi-recette.em-normandie.fr/';
const glpiFormId = import.meta.env.VITE_GLPI_FORM_ID || '5';

const currentTheme = ref('light');
const router = useRouter();
const { t } = useLocale();

// Composables centralisés
const { shouldShowTools, shouldShowQuickSearch, shouldShowChat, shouldShowChatbot } = useLayout();

const routeIsStudentHome = computed(() => {
  return router.currentRoute.value.path === '/home';
});

const navigateToChat = () => {
  router.push('/chat');
};

const navigateToChatbot = () => {
  router.push('/chatbot');
};

const navigateToPartnershipUniversities = () => {
  router.push({ name: 'PartnershipUniversities' });
};

// Mockup mode composable
const { isMockupMode, toggleMockupMode } = useMockupMode();

// Check if we're in development mode
const isDevelopment = computed(() => {
  return import.meta.env.DEV || import.meta.env.MODE === 'development';
});

const onThemeToggled = () => {
  currentTheme.value = currentTheme.value === 'light' ? 'dark' : 'light';
};

onMounted(() => {
  const savedTheme = storageService.getItem('theme');
  if (savedTheme) {
    currentTheme.value = savedTheme;
    if (savedTheme === 'dark') {
      document.body.classList.add('dark');
    }
  }
});

function handleSearchNavigation(result: {
  id: string;
  title: string;
  type: string;
  path?: string;
  url?: string;
}) {
  if (result.type === 'route' && result.path) {
    router.push(result.path);
  } else if ((result.type === 'link' || result.type === 'external') && result.url) {
    window.open(result.url, '_blank');
  }
}

const navigateToBugForm = () => {
  window.open(`${glpiBaseUrl}`, '_blank');
};
</script>

<style scoped>
ion-header {
  transition: all 0.3s ease;
  box-shadow: none;
}

ion-header.drawer-open {
  margin-left: 0;
  width: 100%;
}

ion-title {
  font-size: 1.5rem;
  color: var(--ion-text-color);
}

.desktop-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: var(--student-topbar-height);
  padding-right: 40px;
}
.development-mode-indicator {
  background-color: var(--ion-color-danger);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
}

.drawer-toggle-slot {
  display: flex;
  align-items: center;
}

.navigation-section {
  display: flex;
  align-items: center;
  gap: 20px;
}

.toolbar-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 1;
}

@media (max-width: 768px) {
  ion-header.drawer-open,
  ion-header.drawer-collapsed {
    margin-left: 0;
    width: 100%;
  }

  .drawer-toggle-slot {
    display: none;
  }
}

.mobile-navbar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

ion-button.logbutton {
  margin-right: 10px;
}

.toggle-btn {
  --border-radius: 8px;
  margin-right: 8px;
}

.mockup-toggle-btn::part(native) {
  transition: background-color 0.2s ease;
}

.mockup-toggle-btn:hover::part(native) {
  background-color: rgba(var(--ion-text-color-rgb), 0.15);
}

.mockup-toggle-btn::part(native) {
  transition: background-color 0.2s ease;
}

.mockup-toggle-btn:hover::part(native) {
  background-color: rgba(var(--ion-text-color-rgb), 0.15);
}

.toggle-btn ion-icon {
  font-size: 20px;
}

.toggle-btn[color='primary'] {
  --background: var(--ion-color-primary);
  --color: white;
}

.ward-button {
  padding: 0 6px;
  border: 1px solid var(--ion-color-primary);
  height: 32px;
  border-radius: 3vmin;
}

.ward-button:hover {
  background: var(--ion-background-color-tertiary);
}

.ward-button-title {
  font-size: 14px;
  font-weight: bold;
  color: var(--ion-color-primary);
  padding: 8px;
}
</style>
